import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import GameAssetsContext from '../../../../../context/GameAssetContext';
import './RuneConfig.css';
// Import the ID mapping utilities
import { runeIdMapping } from '../../../../../../NewWebSocket/utils/runeIdMapping';
import { getRuneImageUrlById } from '../../../../../../utils/runeImageMapping';

// Define complete rune trees using IDs instead of names, including shards
interface RuneTreeConfigById {
  treeImage: string;
  KEYSTONES: number[];
  SLOT1: number[];
  SLOT2: number[];
  SLOT3: number[];
}

interface ShardConfigById {
  ROW1: number[];
  ROW2: number[];
  ROW3: number[];
}

interface RunePathsConfigById {
  [key: number]: RuneTreeConfigById;
}

// Rune trees with IDs - much cleaner!
export const RUNE_TREES_BY_ID: RunePathsConfigById = {
  // Domination (8100)
  8100: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7200_Domination.png',
    KEYSTONES: [8112, 8128, 9923], // Electrocute, Dark Harvest, Hail of Blades
    SLOT1: [8126, 8139, 8143], // Cheap Shot, Taste of Blood, Sudden Impact
    SLOT2: [8137, 8140, 8141], // Sixth Sense, Grisly Mementos, Deep Ward
    SLOT3: [8135, 8105, 8106], // Treasure Hunter, Relentless Hunter, Ultimate Hunter
  },
  // Precision (8000)
  8000: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7201_Precision.png',
    KEYSTONES: [8005, 8008, 8021, 8010], // Press the Attack, Lethal Tempo, Fleet Footwork, Conqueror
    SLOT1: [9101, 9111, 8009], // Absorb Life, Triumph, Presence of Mind
    SLOT2: [9104, 9105, 9103], // Legend: Alacrity, Legend: Haste, Legend: Bloodline
    SLOT3: [8014, 8017, 8299], // Coup de Grace, Cut Down, Last Stand
  },
  // Sorcery (8200)
  8200: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7202_Sorcery.png',
    KEYSTONES: [8214, 8229, 8230], // Summon Aery, Arcane Comet, Phase Rush
    SLOT1: [8275, 8226, 8224], // Nimbus Cloak, Manaflow Band, Axiom Arcanist
    SLOT2: [8210, 8234, 8233], // Transcendence, Celerity, Absolute Focus
    SLOT3: [8237, 8232, 8236], // Scorch, Waterwalking, Gathering Storm
  },
  // Resolve (8400)
  8400: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7204_Resolve.png',
    KEYSTONES: [8437, 8439, 8465], // Grasp of the Undying, Aftershock, Guardian
    SLOT1: [8446, 8463, 8401], // Demolish, Font of Life, Shield Bash
    SLOT2: [8429, 8444, 8473], // Conditioning, Second Wind, Bone Plating
    SLOT3: [8451, 8453, 8242], // Overgrowth, Revitalize, Unflinching
  },
  // Inspiration (8300)
  8300: {
    treeImage: 'https://ddragon.canisback.com/img/perk-images/Styles/7203_Whimsy.png',
    KEYSTONES: [8351, 8360, 8369], // Glacial Augment, Unsealed Spellbook, First Strike
    SLOT1: [8306, 8304, 8321], // Hextech Flashtraption, Magical Footwear, Cash Back
    SLOT2: [8313, 8352, 8345], // Triple Tonic, Time Warp Tonic, Biscuit Delivery
    SLOT3: [8347, 8410, 8316], // Cosmic Insight, Approach Velocity, Jack Of All Trades
  }
};

// Shards with IDs
export const SHARD_PATHS_BY_ID: ShardConfigById = {
  ROW1: [5008, 5005, 5007], // Adaptive Force, Attack Speed, Ability Haste
  ROW2: [5008, 5010, 5001], // Adaptive Force, Move Speed, Health Scaling
  ROW3: [5011, 5013, 5001], // Health, Tenacity and Slow Resist, Health Scaling
};

// Shard images by ID
const SHARD_IMAGES_BY_ID: Record<number, string> = {
  5008: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsadaptiveforceicon.png', // Adaptive Force
  5005: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsattackspeedicon.png', // Attack Speed
  5007: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodscdrscalingicon.png', // Ability Haste
  5010: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodsmovementspeedicon.png', // Move Speed
  5001: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodshealthplusicon.png', // Health Scaling
  5011: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodshealthscalingicon.png', // Health
  5013: 'https://raw.communitydragon.org/pbe/plugins/rcp-be-lol-game-data/global/default/v1/perk-images/statmods/statmodstenacityicon.png', // Tenacity and Slow Resist
};

// Note: runeNameToIconKey mapping is now imported from shared utility

// Create mapping for rune tree IDs to icon keys
const treeIdToIconKey: Record<number, string> = {
  8000: 'precision',
  8100: 'domination',
  8200: 'sorcery',
  8400: 'resolve',
  8300: 'inspiration',
};

// Map rune IDs to their slots for secondary path selection
const runeIdToSlotMapping: Record<number, string> = {
  // Precision tree
  9101: "slot1", // Absorb Life
  9111: "slot1", // Triumph
  8009: "slot1", // Presence of Mind
  9104: "slot2", // Legend: Alacrity
  9105: "slot2", // Legend: Haste
  9103: "slot2", // Legend: Bloodline
  8014: "slot3", // Coup de Grace
  8017: "slot3", // Cut Down
  8299: "slot3", // Last Stand

  // Domination tree
  8126: "slot1", // Cheap Shot
  8139: "slot1", // Taste of Blood
  8143: "slot1", // Sudden Impact
  8137: "slot2", // Sixth Sense
  8140: "slot2", // Grisly Mementos
  8141: "slot2", // Deep Ward
  8135: "slot3", // Treasure Hunter
  8105: "slot3", // Relentless Hunter
  8106: "slot3", // Ultimate Hunter

  // Sorcery tree
  8275: "slot1", // Nimbus Cloak
  8226: "slot1", // Manaflow Band
  8224: "slot1", // Axiom Arcanist
  8210: "slot2", // Transcendence
  8234: "slot2", // Celerity
  8233: "slot2", // Absolute Focus
  8237: "slot3", // Scorch
  8232: "slot3", // Waterwalking
  8236: "slot3", // Gathering Storm
  
  // Resolve tree
  8446: "slot1", // Demolish
  8463: "slot1", // Font of Life
  8401: "slot1", // Shield Bash
  8429: "slot2", // Conditioning
  8444: "slot2", // Second Wind
  8473: "slot2", // Bone Plating
  8451: "slot3", // Overgrowth
  8453: "slot3", // Revitalize
  8242: "slot3", // Unflinching
  
  // Inspiration tree
  8306: "slot1", // Hextech Flashtraption
  8304: "slot1", // Magical Footwear
  8321: "slot1", // Cash Back
  8313: "slot2", // Triple Tonic
  8352: "slot2", // Time Warp Tonic
  8345: "slot2", // Biscuit Delivery
  8347: "slot3", // Cosmic Insight
  8410: "slot3", // Approach Velocity
  8316: "slot3", // Jack Of All Trades
};

export type RuneSelection = {
  primaryPath: string;
  primaryRunes: string[];
  secondaryPath: string;
  secondaryRunes: string[];
  shards: string[];
};

interface RuneConfigProps {
  runeSelection: RuneSelection; // Champion's current runes
  onRuneChange: (newRuneSelection: RuneSelection) => void;
}

type RuneSlot = 'keystone' | 'slot1' | 'slot2' | 'slot3';
type ShardRow = 'ROW1' | 'ROW2' | 'ROW3';

const RuneConfig3: React.FC<RuneConfigProps> = ({
  runeSelection,
  onRuneChange,
}) => {
  const { gameWideAssets } = useContext(GameAssetsContext);
  const { runeIcons } = gameWideAssets;
  
  // Helper function to get rune image from context using ID (now using shared utility)
  const getRuneImage = (runeId: number): string => {
    return getRuneImageUrlById(runeId, runeIdMapping, runeIcons);
  };
  
  // Helper function to get shard image by ID
  const getShardImageById = (shardId: number): string => {
    return SHARD_IMAGES_BY_ID[shardId] || '';
  };
  
  // Helper function to get tree name from ID
  const getTreeNameFromId = (treeId: number): string => {
    const treeInfo = runeIdMapping[treeId];
    return treeInfo?.name || "Unknown";
  };
  
  // Helper function to get rune name from ID
  const getRuneNameFromId = (runeId: number): string => {
    const runeInfo = runeIdMapping[runeId];
    return runeInfo?.name || "";
  };
  
  // Helper function to get shard name from ID
  const getShardNameFromId = (shardId: number): string => {
    const shardInfo = runeIdMapping[shardId];
    return shardInfo?.name || "";
  };
  
  // Convert incoming string-based selection to number-based for internal use
  const convertToNumbers = (runeSelection: RuneSelection) => {
    return {
      primaryPath: parseInt(runeSelection.primaryPath) || 8000,
      primaryRunes: runeSelection.primaryRunes.map(id => parseInt(id)).filter(id => !isNaN(id)),
      secondaryPath: parseInt(runeSelection.secondaryPath) || 8100,
      secondaryRunes: runeSelection.secondaryRunes.map(id => parseInt(id)).filter(id => !isNaN(id)),
      shards: runeSelection.shards.map(id => parseInt(id)).filter(id => !isNaN(id))
    };
  };
  
  // Initialize with converted numbers
  const initialSelection = convertToNumbers(runeSelection);
  
  // State for UI selection tracking using IDs
  const [selectedPrimaryPath, setSelectedPrimaryPath] = useState<number>(initialSelection.primaryPath);
  const [selectedSecondaryPath, setSelectedSecondaryPath] = useState<number>(initialSelection.secondaryPath);
  const [selectedShards, setSelectedShards] = useState<Record<ShardRow, number>>({
    ROW1: initialSelection.shards[0] || 0,
    ROW2: initialSelection.shards[1] || 0,
    ROW3: initialSelection.shards[2] || 0,
  });
  
  // State for tracking primary rune selection by slot using IDs
  // Map runes to their correct slots instead of assuming array position
  const getInitialPrimaryRunes = () => {
    const runes: Record<RuneSlot, number> = {
      keystone: 0,
      slot1: 0,
      slot2: 0,
      slot3: 0,
    };
    
    initialSelection.primaryRunes.forEach((runeId, index) => {
      if (index === 0) {
        // First rune is always keystone
        runes.keystone = runeId;
      } else {
        // Map other runes to their correct slots
        const slot = runeIdToSlotMapping[runeId];
        if (slot === 'slot1' || slot === 'slot2' || slot === 'slot3') {
          runes[slot] = runeId;
        }
      }
    });
    
    return runes;
  };
  
  const [selectedPrimaryRunes, setSelectedPrimaryRunes] = useState<Record<RuneSlot, number>>(
    getInitialPrimaryRunes()
  );
  
  // State for tracking secondary rune selection using IDs
  const [selectedSecondaryRunes, setSelectedSecondaryRunes] = useState<number[]>(
    initialSelection.secondaryRunes
  );
  
  // Add a ref to track the button
  const saveButtonRef = useRef<HTMLButtonElement>(null);
  
  // Check if current selections differ from the original runes
  const hasChanges = useMemo(() => {
    const current = {
      primaryPath: selectedPrimaryPath,
      primaryRunes: Object.values(selectedPrimaryRunes),
      secondaryPath: selectedSecondaryPath,
      secondaryRunes: selectedSecondaryRunes,
      shards: Object.values(selectedShards)
    };
    
    const original = {
      primaryPath: initialSelection.primaryPath,
      primaryRunes: initialSelection.primaryRunes,
      secondaryPath: initialSelection.secondaryPath,
      secondaryRunes: initialSelection.secondaryRunes,
      shards: initialSelection.shards
    };
    
    return JSON.stringify(current) !== JSON.stringify(original);
  }, [selectedPrimaryPath, selectedSecondaryPath, selectedPrimaryRunes, selectedSecondaryRunes, selectedShards, initialSelection]);

  // Check if the runes selection is valid
  const isValidRuneSelection = useMemo(() => {
    const hasPrimaryPath = !!selectedPrimaryPath;
    const primarySelectedCount = Object.values(selectedPrimaryRunes).filter(id => id > 0).length;
    const hasSecondaryPath = !!selectedSecondaryPath && selectedSecondaryPath !== selectedPrimaryPath;
    const secondarySelectedCount = selectedSecondaryRunes.filter(id => id > 0).length;
    const shardSelectedCount = Object.values(selectedShards).filter(id => id > 0).length;
    
    return hasPrimaryPath && primarySelectedCount === 4 && hasSecondaryPath && secondarySelectedCount >= 2 && shardSelectedCount === 3;
  }, [selectedPrimaryPath, selectedSecondaryPath, selectedPrimaryRunes, selectedSecondaryRunes, selectedShards]);
  
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  
  const handleSave = () => {
    if (!isValidRuneSelection) {
      setShowValidationMessage(true);
      setTimeout(() => setShowValidationMessage(false), 3000);
      return;
    }
    
    // Convert back to strings for the API
    const newRuneSelection: RuneSelection = {
      primaryPath: selectedPrimaryPath.toString(),
      primaryRunes: Object.values(selectedPrimaryRunes).filter(id => id > 0).map(id => id.toString()),
      secondaryPath: selectedSecondaryPath.toString(),
      secondaryRunes: selectedSecondaryRunes.filter(id => id > 0).map(id => id.toString()),
      shards: Object.values(selectedShards).filter(id => id > 0).map(id => id.toString())
    };

    // Visual feedback
    if (saveButtonRef.current) {
      saveButtonRef.current.classList.add('pulse-animation');
      setTimeout(() => {
        if (saveButtonRef.current) {
          saveButtonRef.current.classList.remove('pulse-animation');
        }
      }, 2000);
    }

    onRuneChange(newRuneSelection);
  };

  const handleSelectPrimaryRune = (slot: RuneSlot, runeId: number) => {
    setSelectedPrimaryRunes(prevSelectedRunes => ({
      ...prevSelectedRunes,
      [slot]: runeId,
    }));
  };

  const handleSelectSecondaryRune = (runeId: number) => {
    setSelectedSecondaryRunes(prev => {
      const updated = [...prev];
      const existingIndex = updated.indexOf(runeId);
      
      if (existingIndex >= 0) {
        // Already selected, remove it
        updated.splice(existingIndex, 1);
      } else {
        // Not selected, add it
        if (updated.length >= 2) {
          // Remove oldest selection
          updated.shift();
        }
        updated.push(runeId);
      }
      
      return updated;
    });
  };
  
  const handleShardSelect = (rowKey: ShardRow, shardId: number) => {
    setSelectedShards(prev => ({
      ...prev,
      [rowKey]: shardId
    }));
  };

  const handlePrimaryRunePathSelect = (pathId: number) => {
    if (pathId === selectedPrimaryPath) return;
    
    // If selecting path that's currently secondary, clear secondary
    if (pathId === selectedSecondaryPath) {
      setSelectedSecondaryPath(0);
      setSelectedSecondaryRunes([]);
    }
    
    // Update primary path and reset primary runes
    setSelectedPrimaryPath(pathId);
    setSelectedPrimaryRunes({
      keystone: 0,
      slot1: 0,
      slot2: 0,
      slot3: 0
    });
  };
  
  const handleSecondaryRunePathSelect = (pathId: number) => {
    if (pathId === selectedSecondaryPath) return;
    if (pathId === selectedPrimaryPath) return; // Don't allow same primary and secondary
    
    setSelectedSecondaryPath(pathId);
    setSelectedSecondaryRunes([]);
  };

  const renderPrimaryRunePath = (pathId: number) => {
    const runeData = RUNE_TREES_BY_ID[pathId];
    
    if (!runeData) {
      console.error(`Path ${pathId} not found in rune configuration`);
      return <div className="rc-error">Error loading runes</div>;
    }
    
    return (
      <>
        <div className="rc-keystone-row">
          {runeData.KEYSTONES.map((runeId, index) => {
            const selected = selectedPrimaryRunes.keystone === runeId;
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div
                key={index}
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`}
                onClick={() => handleSelectPrimaryRune('keystone', runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
        <div className="rc-slot1-row">
          {runeData.SLOT1.map((runeId, index) => {
            const selected = selectedPrimaryRunes.slot1 === runeId;
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div
                key={index}
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`}
                onClick={() => handleSelectPrimaryRune('slot1', runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
        <div className="rc-slot2-row">
          {runeData.SLOT2.map((runeId, index) => {
            const selected = selectedPrimaryRunes.slot2 === runeId;
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div
                key={index}
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`}
                onClick={() => handleSelectPrimaryRune('slot2', runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
        <div className="rc-slot3-row">
          {runeData.SLOT3.map((runeId, index) => {
            const selected = selectedPrimaryRunes.slot3 === runeId;
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div
                key={index}
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`}
                onClick={() => handleSelectPrimaryRune('slot3', runeId)}
              >
                <img 
                  src={getRuneImage(runeId)}
                  alt={runeName}
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderSecondaryRunePath = (pathId: number) => {
    const runeData = RUNE_TREES_BY_ID[pathId];

    if (!runeData) {
      console.error(`Path ${pathId} not found in rune configuration`);
      return <div className="rc-error">Error loading runes</div>;
    }

    // Helper to check if a secondary slot should be greyed out
    const shouldGreyOutSlot = (slot: string): boolean => {
      if (selectedSecondaryRunes.length < 2) return false;
      
      // Check if any of the currently selected runes are in this slot
      return !selectedSecondaryRunes.some(runeId => runeIdToSlotMapping[runeId] === slot);
    };

    return (
      <>
        <div className={`rc-slot1-row ${shouldGreyOutSlot('slot1') ? 'rc-row-greyed-out' : ''}`}>
          {runeData.SLOT1.map((runeId, index) => {
            const selected = selectedSecondaryRunes.includes(runeId);
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div 
                key={index} 
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`} 
                onClick={() => handleSelectSecondaryRune(runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
        <div className={`rc-slot2-row ${shouldGreyOutSlot('slot2') ? 'rc-row-greyed-out' : ''}`}>
          {runeData.SLOT2.map((runeId, index) => {
            const selected = selectedSecondaryRunes.includes(runeId);
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div 
                key={index} 
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`} 
                onClick={() => handleSelectSecondaryRune(runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
        <div className={`rc-slot3-row ${shouldGreyOutSlot('slot3') ? 'rc-row-greyed-out' : ''}`}>
          {runeData.SLOT3.map((runeId, index) => {
            const selected = selectedSecondaryRunes.includes(runeId);
            const runeName = getRuneNameFromId(runeId);
            
            return (
              <div 
                key={index} 
                className={`rc-rune-slot ${selected ? 'selected' : 'unselected-grey'}`} 
                onClick={() => handleSelectSecondaryRune(runeId)}
              >
                <img 
                  src={getRuneImage(runeId)} 
                  alt={runeName} 
                  title={runeName}
                />
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderShardRow = (rowKey: ShardRow) => {
    const shards = SHARD_PATHS_BY_ID[rowKey];
    const selectedShard = selectedShards[rowKey];
    
    return (
      <div className="rc-shard-row">
        {shards.map(shardId => {
          const isSelected = selectedShard === shardId;
          const imageSrc = getShardImageById(shardId);
          const shardName = getShardNameFromId(shardId);
          
          return (
            <div
              key={shardId}
              className={`rc-shard-slot ${isSelected ? 'selected' : 'unselected'}`}
              onClick={() => handleShardSelect(rowKey, shardId)}
            >
              {imageSrc ? (
                <img src={imageSrc} alt={shardName} title={shardName} />
              ) : (
                <span style={{color: 'white'}}>{shardName}</span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="rc-container">
      
      <div className="rc-wrapper">
        <div className="rc-primary-section">
          <div className="rc-path-selector">
            {Object.entries(RUNE_TREES_BY_ID).map(([pathIdStr, { treeImage }]) => {
              const pathId = parseInt(pathIdStr);
              const treeIconKey = treeIdToIconKey[pathId];
              const treeIcon = treeIconKey && runeIcons[treeIconKey] ? runeIcons[treeIconKey] : treeImage;
              const treeName = getTreeNameFromId(pathId);
              
              return (
                <button
                  key={`primary-${pathId}`}
                  className={`rc-path-button primary-path-button ${selectedPrimaryPath === pathId ? 'selected' : ''}`}
                  onClick={() => handlePrimaryRunePathSelect(pathId)}
                  title={treeName}
                >
                  <img src={treeIcon} alt={treeName} />
                </button>
              );
            })}
          </div>
          {selectedPrimaryPath && renderPrimaryRunePath(selectedPrimaryPath)}
        </div>

        <div className="rc-section-divider"></div>

        <div className="rc-secondary-section">
          <div className="rc-path-selector">
            {Object.entries(RUNE_TREES_BY_ID).map(([pathIdStr, { treeImage }]) => {
              const pathId = parseInt(pathIdStr);
              const treeIconKey = treeIdToIconKey[pathId];
              const treeIcon = treeIconKey && runeIcons[treeIconKey] ? runeIcons[treeIconKey] : treeImage;
              const treeName = getTreeNameFromId(pathId);
              
              return (
                <button
                  key={`secondary-${pathId}`}
                  className={`rc-path-button secondary-path-button ${selectedSecondaryPath === pathId ? 'selected' : ''} ${pathId === selectedPrimaryPath ? 'disabled' : ''}`}
                  onClick={() => handleSecondaryRunePathSelect(pathId)}
                  style={{ 
                    opacity: pathId === selectedPrimaryPath ? 0.5 : 1
                  }}
                  title={treeName}
                  disabled={pathId === selectedPrimaryPath}
                >
                  <img src={treeIcon} alt={treeName} />
                </button>
              );
            })}
          </div>
          {selectedSecondaryPath && renderSecondaryRunePath(selectedSecondaryPath)}
          
          <div className="rc-shard-container">
            <h5 style={{ color: 'white', textAlign: 'center', margin: '10px 0' }}>Stat Shards</h5>
            {(Object.keys(SHARD_PATHS_BY_ID) as ShardRow[]).map(rowKey => renderShardRow(rowKey))}
          </div>
        </div>
      </div>
      
      <div className="rc-button-container">
        <button 
          ref={saveButtonRef}
          className={`rc-save-button ${!hasChanges ? 'no-changes' : ''} ${!isValidRuneSelection ? 'invalid-selection' : ''}`} 
          onClick={handleSave}
          disabled={!hasChanges || !isValidRuneSelection}
          title={!isValidRuneSelection ? 'You must select all 4 primary runes, 2 secondary runes, and 3 stat shards' : 
                 !hasChanges ? 'No changes to apply' : 
                 'Apply your rune changes'}
        >
          Apply Runes
        </button>
        {showValidationMessage && (
          <div className="rc-validation-message" style={{ color: 'white' }}>
            Please select all 4 primary runes, 2 secondary runes, and 3 stat shards
          </div>
        )}
      </div>
    </div>
  );
};

export default RuneConfig3; 